import { type UseMutationOptions, useMutation, useQuery } from "@tanstack/react-query"
import customInstance from "@/api/instance"
import type {
  ForgetPasswordResponse,
  ResetPasswordResponse,
  SessionResponse,
  SignInEmailRequest,
  SignInEmailResponse,
} from "./models"
import {
  type ForgetPasswordRequest,
  forgetPasswordRequestSchema,
  type ResetPasswordRequest,
  resetPasswordRequestSchema,
  signInEmailRequestSchema,
} from "./schemas"

/**
 * サインインを行うカスタムフック
 * @param options
 * @returns
 */
export function useSignInEmail(
  options?: UseMutationOptions<SignInEmailResponse, Error, SignInEmailRequest>
) {
  return useMutation({
    mutationFn: (data: SignInEmailRequest) => {
      const validatedData = signInEmailRequestSchema.parse(data)

      return customInstance<SignInEmailResponse>("/api/auth/sign-in/email", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}

/**
 * サインアウトを行うカスタムフック
 * @param options
 * @returns
 */
export function useSignOut(options?: UseMutationOptions<{ success: boolean }, Error>) {
  return useMutation({
    mutationFn: () => {
      return customInstance<{ success: boolean }>("/api/auth/sign-out", {
        method: "POST",
        body: JSON.stringify({}),
      })
    },
    ...options,
  })
}

/**
 * セッション取得用カスタムフック
 */
export function useGetSession() {
  return useQuery<SessionResponse>({
    queryKey: ["get-session"],
    queryFn: async () =>
      customInstance<SessionResponse>("/api/auth/get-session", {
        method: "GET",
      }),
  })
}

/**
 * パスワードリセットメールを送信するカスタムフック
 * @param options
 * @returns
 */
export function useForgetPassword(
  options?: UseMutationOptions<ForgetPasswordResponse, Error, ForgetPasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ForgetPasswordRequest) => {
      const validatedData = forgetPasswordRequestSchema.parse(data)
      const redirectTo = `${window.location.origin}/password-reset`

      return customInstance<ForgetPasswordResponse>("/api/auth/request-password-reset", {
        method: "POST",
        body: JSON.stringify({
          ...validatedData,
          redirectTo,
        }),
      })
    },
    ...options,
  })
}

/**
 * パスワードをリセットするカスタムフック
 * @param options
 * @returns
 */
export function useResetPassword(
  options?: UseMutationOptions<ResetPasswordResponse, Error, ResetPasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => {
      const validatedData = resetPasswordRequestSchema.parse(data)

      return customInstance<ResetPasswordResponse>("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface ChangePasswordResponse {
  success: boolean
  message?: string
}

/**
 * パスワードを変更するカスタムフック（ログイン中のユーザー用）
 * @param options
 * @returns
 */
export function useChangePassword(
  options?: UseMutationOptions<ChangePasswordResponse, Error, ChangePasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => {
      return customInstance<ChangePasswordResponse>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
          revokeOtherSessions: false,
        }),
      })
    },
    ...options,
  })
}
